import {lazy, useCallback, useContext, useState, useEffect} from 'react';
import {useSearchParams} from 'react-router-dom';
import {useAuthStore} from '@/store/auth_store';
import {useRosterStore} from '@/store/roster_store';
import Wrapper from '@common/pages/Wrapper';
import RemoteComponent from '@common/RemoteComponent';
import {CarecoApiContext} from "@/app-context/careco-api-context";
import {useClientStore} from "@/store/client_store";
import {ClientProfileResponse, ClientProfile, JobStatus} from "@/api/dto/client-profile";

const ReferralIndex = lazy(() => import('thoth/ReferralIndex'));

export default function ReferralsPage() {
  const [searchParams] = useSearchParams();
  const authStore = useAuthStore();
  const rosterStore = useRosterStore();
  const {carecoApi} = useContext(CarecoApiContext);

  const patientPingId = searchParams.get('p');
  const referralId = searchParams.get('referral');
  const setCurrentClient = useClientStore((state) => state.setCurrentClient);
  const [clientProfile, setClientProfile] = useState<ClientProfileResponse>();

  const view = patientPingId || referralId ? 'create' : 'list';

  const getClientProfile = useCallback(
    async (id: string): Promise<ClientProfile | null> => {
      if (!rosterStore.currentRoster || !carecoApi) return null;

      let response: ClientProfileResponse;

      do {
        response = await carecoApi.getClientProfile(
          id, rosterStore.currentRoster.id, true, 1, false
        );
        await new Promise(r => setTimeout(
          r, Number(process.env.REACT_APP_CLIENT_PROFILE_POLL_DELAY) || 500
        ));
      } while (
        response.patientProfileJobResult?.status === JobStatus.InProgress ||
        response.patientProfileJobResult?.status === JobStatus.Retrying
      );

      return response.patientProfileResponse ?? null;
    }, [rosterStore.currentRoster, carecoApi]
  );

  useEffect(() => {
    let cancelled = false;

    if (patientPingId && rosterStore.currentRoster && carecoApi) {
      setClientProfile(undefined);
      getClientProfile(patientPingId).then((profile) => {
        if (!cancelled && profile) {
          setCurrentClient(profile);
          setClientProfile(
            { patientProfileResponse: profile } as ClientProfileResponse
          );
        }
      });
    }

    return () => { cancelled = true; };
  }, [patientPingId, rosterStore.currentRoster, carecoApi]);

  return(
    <Wrapper>
      <div style={{ padding: '8px'}}>
        {(!patientPingId || clientProfile?.patientProfileResponse) && (
          <RemoteComponent
            key={view}
            remote="thoth"
            component={ReferralIndex}
            authStore={authStore}
            rosterStore={rosterStore}
            patientPingId={patientPingId}
            referralId={referralId}
            patientPing={clientProfile?.patientProfileResponse}
            getClientProfile={getClientProfile}
            view={view}
          />
        )}
      </div>
    </Wrapper>
  );
}
